'use client';

import type { PostMediaItem } from '@ps/api/types';
import { PostContentTypeEnumItem } from '@ps/api/types';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import { blurhashToDataUrl } from '@ps/ui/utils/blurhashToDataUrl';
import { PlayIcon } from 'lucide-react';

import { getPostMediaCounts } from '../functions/getPostMediaCounts';
import { postMediaCountLabel } from '../functions/postMediaCountLabel';
import { restrictedMedias } from '../functions/restricted-media';
import { PostPaywall } from '../post-view/components/paywall/PostPaywall';
import {
  type PostGatingInput,
  buildPostGating,
} from '../post-view/functions/postViewModel';

/**
 * Projection of a post the chat card renders. Both the fetched `PostSearchItem`
 * AND the lighter default models a caller derives from a message snapshot (DM
 * `PostMessageContent`, community `MessagePostLink`) satisfy this shape — so
 * every consumer renders ONE `PostChatMessageView`, no bespoke "lite" fallback.
 *
 * Paywall source (`restricted` + `*ToView` + `unrestrictedLeadingMediaCount`)
 * lives ONLY on the fetched post; snapshots omit it, so a default model simply
 * renders un-gated until the fetch lands (its media is blurhash-only anyway).
 * `imageCount`/`videoCount` are the server's own counts, used as the cover-badge
 * source when `medias` is absent (the DM snapshot carries counts, not media).
 */
export type PostCardData = {
  title?: string;
  description?: string;
  postType: PostContentTypeEnumItem;
  medias?: PostMediaItem[];
  restricted?: boolean;
  imageCount?: number;
  videoCount?: number;
} & Partial<PostGatingInput>;

// Default cover blurhash for a media post whose media hasn't loaded yet (e.g.
// the DM snapshot carries no media) — ported from PopshopRN `usePostImageInfo`.
// Rendering it reserves the aspect-3/2 cover box so the card doesn't layout-shift
// when the fetched media lands. The chat bubble is dark-surfaced, so we use the
// dark variant (light: 'U6PZfSi_.AyE_3t7t7R**0o#DgR4_3R*D%xt').
const kDefaultCoverBlurhash = 'U14o1dRj00%Mt7RjIU%M00t7~qIU%MayRjof';

interface Props {
  post: PostCardData;
  /**
   * Marks the card as belonging to a sent (own) message. Currently
   * cosmetic — the upstream PopshopRN card looks identical on both sides.
   */
  isOwn?: boolean;
  /**
   * When true, the card stretches to fill its parent's width (`w-full`) instead
   * of capping at the chat-bubble preferred size.
   */
  fullWidth?: boolean;
  /** Soft cap on title/description lines for tight bubbles. */
  titleLineClamp?: number;
  descriptionLineClamp?: number;
  /**
   * Open the post-detail modal. Fired by clicking the card body — anywhere
   * EXCEPT a link or a button (the paywall CTAs). When omitted the card body is
   * inert. Also backs the "Read More / View All" cue.
   */
  onOpen?: () => void;
  /**
   * Live paywall unlock actions. When provided, the card's paywall CTAs launch
   * the subscribe / pay / tip flow DIRECTLY (the button click is excluded from
   * the card-body `onOpen`). Omitted → the CTAs render inert (a pure preview).
   */
  onSubscribe?: (level: PopStoreExtendedSubscriptionLevel | undefined) => void;
  onPay?: () => void;
  onTip?: () => void;
}

/**
 * Rich preview of a Post inside a chat bubble (DM + community). Cover +
 * media-count badge for galleries, title + description, mint "Read More / View
 * All". For a gated post it renders the full {@link PostPaywall} — overlaid on
 * the cover for a media gallery, or as a placeholder block for a rich-text post
 * — mirroring the wall's `GatedPostContent`. The fetched post is viewer-scoped
 * (the search API resolves access + redacts media per viewer), so no extra
 * un-redaction layer is needed here.
 */
export function PostChatMessageView({
  post,
  fullWidth,
  titleLineClamp = 1,
  descriptionLineClamp = 3,
  onOpen,
  onSubscribe,
  onPay,
  onTip,
}: Props) {
  const isGallery = post.postType === PostContentTypeEnumItem.MediaGallery;

  const gating = buildPostGating(post);
  const restricted = post.restricted === true;
  // 1:1 with upstream `getShouldShowLocked`: lock a restricted post only when
  // it's rich text OR has no free leading media to preview.
  const showLocked = restricted && (!isGallery || !gating.openCount);

  // Blank gated media URLs before they hit the DOM (past the free window).
  const medias = restrictedMedias({
    medias: post.medias,
    restricted: post.restricted,
    unrestrictedLeadingMediaCount: post.unrestrictedLeadingMediaCount,
  });

  // Prefer the first IMAGE as the cover even in a mixed gallery — matches
  // PopshopRN; image loads are more reliable than <video> posters.
  const cover = isGallery
    ? (medias.find(m => m.type === 'IMAGE') ?? medias[0])
    : undefined;
  const hasAnyVideo =
    isGallery && (medias.some(m => m.type !== 'IMAGE') ?? false);
  const coverSources = cover ? extractCoverSources(cover) : undefined;

  // Counts from the actual media when present, else the snapshot's own counts.
  const counts =
    medias.length > 0
      ? getPostMediaCounts(medias)
      : { imageCount: post.imageCount ?? 0, videoCount: post.videoCount ?? 0 };
  const mediaSummary = [
    postMediaCountLabel(counts.imageCount, 'image'),
    postMediaCountLabel(counts.videoCount, 'video'),
  ]
    .filter(Boolean)
    .join(' / ');

  const readMoreText = isGallery ? 'View All …' : 'Read More …';
  const description = post.description?.trim();
  const title = post.title?.trim() || 'Post';

  // A media post ALWAYS renders the aspect-3/2 cover box; its background is the
  // media's blurhash (or the generic default when there's none — e.g. a snapshot
  // pre-fetch), so the box reserves space and never layout-shifts. The real
  // video frame / image paints ON TOP of this background.
  const coverBackgroundUrl = isGallery
    ? (coverSources?.blurhashUrl ??
      blurhashToDataUrl(kDefaultCoverBlurhash, 32, 21))
    : undefined;

  return (
    <div
      // Width follows PopshopRN's `useMessageMaxWidth` rule for chat bubbles —
      // `min(315px, 55% of the message-list inline size)`; list-row consumers
      // pass `fullWidth`. `overflow-hidden` clips the cover to the card corners.
      className={`bg-surface-1 flex flex-col overflow-hidden rounded-2xl ${fullWidth ? 'w-full' : 'w-[min(315px,55cqw)]'} ${onOpen ? 'cursor-pointer' : ''}`}
      // Card-body tap opens the post-detail modal, but a tap on a paywall CTA
      // (a `<button>`) or any link launches its own action instead — so the
      // `closest('a, button')` guard lets the live unlock CTAs fire without also
      // opening the modal.
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={
        onOpen
          ? event => {
              if ((event.target as HTMLElement).closest('a, button')) return;
              onOpen();
            }
          : undefined
      }
      onKeyDown={
        onOpen
          ? event => {
              if (
                (event.key === 'Enter' || event.key === ' ') &&
                event.target === event.currentTarget
              ) {
                event.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
    >
      {isGallery ? (
        <div
          className="bg-surface-3 relative aspect-[3/2] w-full overflow-hidden bg-cover bg-center"
          style={
            coverBackgroundUrl
              ? { backgroundImage: `url("${coverBackgroundUrl}")` }
              : undefined
          }
        >
          {coverSources?.videoUrl ? (
            // No blurhash `poster` — a poster sits ON TOP and would hide the
            // frame; the blurhash is the box background instead, so the video's
            // first frame (or the real `posterUrl` thumbnail) shows.
            <video
              src={coverSources.videoUrl}
              poster={coverSources.posterUrl}
              muted
              playsInline
              preload="metadata"
              className="h-full w-full object-cover"
            />
          ) : coverSources?.posterUrl ? (
            <img
              src={coverSources.posterUrl}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : null}
          {hasAnyVideo && !showLocked ? (
            <div className="bg-scrim/20 absolute inset-0 flex items-center justify-center">
              <PlayIcon className="h-8 w-8 fill-white text-white" aria-hidden />
            </div>
          ) : null}
          {mediaSummary && !showLocked ? (
            <span className="bg-scrim/60 absolute bottom-2 left-2 rounded-full px-2 py-0.5 text-[11px] font-medium text-white">
              {mediaSummary}
            </span>
          ) : null}
          {showLocked ? (
            <PostPaywall
              gating={gating}
              compact
              onSubscribe={onSubscribe}
              onPay={onPay}
              onTip={onTip}
            />
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5 px-3.5 pb-3 pt-3">
        <p
          className="text-content-1 text-sm font-semibold"
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: titleLineClamp,
            overflow: 'hidden',
          }}
        >
          {title}
        </p>
        {description ? (
          <p
            className="text-content-2 text-xs leading-snug"
            style={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: descriptionLineClamp,
              overflow: 'hidden',
            }}
          >
            {description}
          </p>
        ) : null}

        {/* Rich-text posts have no cover to overlay — give the lock its own
            placeholder block (mirrors the wall's `GatedPostContent`). `-mx-3.5`
            cancels the card body padding so the overlay spans the full card
            width — same button room as the media-cover overlay, so the CTA
            doesn't wrap when the cover version wouldn't. */}
        {showLocked && !isGallery ? (
          <div className="relative -mx-3.5 mt-1 min-h-[120px]">
            <PostPaywall
              gating={gating}
              compact
              onSubscribe={onSubscribe}
              onPay={onPay}
              onTip={onTip}
            />
          </div>
        ) : null}

        {onOpen ? (
          // Visual cue only — the whole card opens the modal, so this stays a
          // non-interactive span (no nested button inside the clickable card).
          <span className="text-brand-text self-start pt-0.5 text-[11px] font-semibold">
            {readMoreText}
          </span>
        ) : null}
      </div>
    </div>
  );
}

interface CoverSources {
  /** A real image to paint on top — image `src`, or a video's server thumbnail.
   *  NEVER a blurhash (that's `blurhashUrl`) and NEVER the video file. */
  posterUrl: string | undefined;
  /** Playable video file — rendered as `<video>` so its first frame shows. */
  videoUrl: string | undefined;
  /** Decoded blurhash placeholder — the cover-box background, so it shows during
   *  video load / when the media is redacted, and the real frame paints over it. */
  blurhashUrl: string | undefined;
}

/**
 * Split a `PostMediaItem` into a real poster image, a playable video url, and a
 * blurhash placeholder. Keeping the blurhash SEPARATE from the poster is what
 * lets a video show its real first frame: the blurhash is the box background,
 * not a `poster` that would sit on top of (and hide) the frame.
 */
function extractCoverSources(media: PostMediaItem): CoverSources {
  const blurhashUrl = blurhashPlaceholderFromMedia(media);
  if (media.type === 'IMAGE') {
    return {
      posterUrl:
        media.processedUrl || media.url || media.thumbnailUrl || undefined,
      videoUrl: undefined,
      blurhashUrl,
    };
  }
  // Video / LiveReplay — `thumbnailUrl` is the only safe <img> poster;
  // processedUrl / url are the video FILE (played by `<video>`, which shows its
  // first frame).
  return {
    posterUrl: media.thumbnailUrl || undefined,
    videoUrl: media.processedUrl || media.url || undefined,
    blurhashUrl,
  };
}

/**
 * Decode `metadata.blurhash` into a small data-URL sized to the media's aspect
 * ratio. The browser scales it up — blurhashes are blurry by design so the
 * low-res decode is invisible.
 */
function blurhashPlaceholderFromMedia(
  media: PostMediaItem,
): string | undefined {
  const blurhash = media.metadata?.blurhash;
  if (!blurhash) return undefined;
  const width = media.metadata?.dimensions?.width;
  const height = media.metadata?.dimensions?.height;
  const ratio = width && height && height > 0 ? width / height : 1;
  const decodeWidth = 32;
  const decodeHeight = Math.max(16, Math.round(decodeWidth / ratio));
  return blurhashToDataUrl(blurhash, decodeWidth, decodeHeight);
}
